import { Download, Description } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';

import type { IKiDownloadProposal } from '../Interfaces/IKi';

/**
 * Eine Datei, die die KI erzeugt hat — zum Herunterladen (#1336).
 *
 * Entsteht heute bei der Umwandlung PDF → Word. Die Datei kommt fertig aus
 * DiNo und war nie beim Anbieter: das Modell entscheidet nur, **dass**
 * umgewandelt wird, den Inhalt bekommt es nicht zu sehen.
 *
 * Der Hinweis aus der Umwandlung steht sichtbar auf der Karte. Eine Word-Datei
 * aus einem PDF ist nie eine originalgetreue Kopie, und wer das erst nach dem
 * Öffnen merkt, hat schon damit gearbeitet.
 */

export const KiDownloadProposalCard = ({
  proposal,
}: {
  proposal: IKiDownloadProposal;
}) => {
  const { FileName, MediaType, Base64, Pages, Note } = proposal.Data;

  // Aus dem Base64 eine Adresse machen, über die der Browser speichern kann.
  // useMemo, damit nicht bei jedem Neuzeichnen eine weitere entsteht.
  const url = useMemo(() => {
    try {
      const binary = atob(Base64);
      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      return URL.createObjectURL(new Blob([bytes], { type: MediaType }));
    } catch {
      return null;
    }
  }, [Base64, MediaType]);

  // Die Adresse wieder freigeben, sonst hält der Verlauf jede erzeugte Datei
  // im Speicher — bei mehreren Umwandlungen je Sitzung summiert sich das.
  useEffect(() => {
    return () => {
      if (url !== null) {
        URL.revokeObjectURL(url);
      }
    };
  }, [url]);

  if (url === null) {
    return (
      <Alert severity='error' sx={{ mt: 1 }}>
        Die erzeugte Datei konnte nicht gelesen werden.
      </Alert>
    );
  }

  return (
    <Alert
      icon={<Description fontSize='inherit' />}
      severity='success'
      variant='outlined'
      sx={{ mt: 1 }}
      action={
        <Button
          size='small'
          variant='contained'
          startIcon={<Download />}
          component='a'
          href={url}
          download={FileName}
        >
          Herunterladen
        </Button>
      }
    >
      <Box>
        <Typography variant='body2'>
          <b>{FileName}</b>
          {Pages ? ` · ${Pages} Seite${Pages === 1 ? '' : 'n'}` : ''}
        </Typography>
        {Note !== undefined && Note !== '' && (
          <Typography
            variant='caption'
            sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
          >
            {Note}
          </Typography>
        )}
      </Box>
    </Alert>
  );
};
